import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { withAuthorPanelSwitch, withStandardBaseCssClass } from '@adobe/aem-core-components-react-spa';
import { useTrainingsLibrary } from '../../../../lib/api/swrApi';
import {
  filterDataToParams,
  mergeFilterData,
  onlySelectedFacets,
  withPageSize,
} from '../../../../lib/utils/search/searchHelper';
import { FacetType, FilterData } from '../../../../lib/utils/search/searchModel';
import { pipe } from '../../../../lib/utils/functional';
import { useSearchState } from '../../../../lib/utils/search/useSearchState';
import FilteredCardsView from '../../base/FilteredCardsView/FilteredCardsView';
import FiltersViewContext, { FiltersInitialViewContextType } from '../../../../lib/context/FiltersViewContext';
import { pushAnalyticsEvent } from '../../../../lib/utils/analytics/analytics';
import { internalSearchTracking } from '../../../../lib/utils/analytics/analyticsHelper';
import { isNil } from '../../../../lib/utils/isNil';
import { isCourseAndUserProgress } from '../../../../lib/api/apiAdatper';
import useFeature from '../../../../lib/hooks/useFeature';
import { LearningLibraryProps } from './Learning.types';
import { FeatureToggleCodename } from '../../../../lib/types/enums';
import { Api } from '../../../../lib/types/apiSpec';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

export const searchResultsToCardProps = (
  items: (Api.TrainingObject | Api.MicrolearningLibrary | Api.VideoLibrary | Api.DocumentLibrary | Api.EventLibrary)[],
) =>
  items.map(props => ({
    ...props,
    lastUpdate: undefined,
    ...('lastUpdate' in props && props.lastUpdate && { lastUpdate: new Date(props.lastUpdate) }),
    ...('lastUpdateDate' in props && props.lastUpdateDate && { lastUpdate: new Date(props.lastUpdateDate) }),
    ...(isCourseAndUserProgress(props) && props.compliantTill && { compliantTill: new Date(props.compliantTill) }),
  })) || [];

const Learning = (props: LearningLibraryProps) => {
  const { tabConfig, pageSize, hideTabs } = props;
  const previousCall = useRef<AbortController | undefined>();
  const isInitialLoad = useRef<boolean>(true);
  const url = useLocation();
  const hideOverdue = useFeature(FeatureToggleCodename.hideOverdueDates);
  const { getFilteredErrorMessage } = useErrorMapping();

  const getSignal = () => {
    if (previousCall.current) {
      previousCall.current.abort();
    }
    previousCall.current = new AbortController();

    return previousCall.current.signal;
  };

  const { urlFilters, updateFilters, clearFilters, paramsReady, isInitialTeamsLoading, activeTab, handleChangeTab } =
    useSearchState(tabConfig, !!hideOverdue, true);

  const { data, isLoading, isValidating, error, sortByOptions } = useTrainingsLibrary({
    filterData: {
      ...urlFilters,
      pageSize,
    },
    activeTab,
    getSignal,
    shouldFetch: paramsReady,
  });

  const filterFacets: FilterData<typeof FacetType.Checkbox> = pipe<FilterData<typeof FacetType.Checkbox>>(
    mergeFilterData((data?.meta as FilterData<typeof FacetType.Checkbox>) || urlFilters, activeTab?.contentTypes),
    onlySelectedFacets(activeTab?.filterGroups ?? []),
    withPageSize(pageSize),
  )(urlFilters);

  const trackedFacets = useMemo(() => filterDataToParams({ ...filterFacets, page: undefined }), [filterFacets]);

  const courses = useMemo(() => searchResultsToCardProps(data?.trainings ?? []) ?? [], [data?.trainings]);

  useEffect(() => {
    if (!isLoading && ((filterFacets.page ?? 1) === 1 || isInitialLoad.current)) {
      const urlWithFacets = [url.pathname, trackedFacets].filter(Boolean).join('?');
      const fullUrl = `${urlWithFacets}${url.hash ?? ''}`;
      const numberOfResults = filterFacets.totalCount ?? 0;

      pushAnalyticsEvent(internalSearchTracking(numberOfResults, fullUrl));
      isInitialLoad.current = false;
    }
  }, [isLoading, trackedFacets]);

  const loading: FiltersInitialViewContextType['loading'] = (() => {
    if ((isLoading && isNil(data)) || isInitialTeamsLoading) {
      return 'initial';
    }
    if (isValidating && !isNil(data)) {
      return 'refresh';
    }

    return null;
  })();

  const filterViewContext = useMemo<FiltersInitialViewContextType>(
    () => ({
      ...props,
      tabConfig,
      from: ((filterFacets.page ?? 1) - 1) * pageSize + 1,
      to: Math.min((filterFacets.page ?? 1) * pageSize, filterFacets.totalCount ?? 0),
      filters: filterFacets,
      updateFilters,
      onClear: clearFilters,
      courses,
      withSearch: true,
      loading,
      fetchingError: getFilteredErrorMessage(error),
      sortByOptions,
      activeTab,
      handleChangeTab,
      hideTabs,
    }),
    [filterFacets, loading, error, activeTab],
  );

  return (
    <FiltersViewContext value={filterViewContext}>
      <FilteredCardsView />
    </FiltersViewContext>
  );
};
export default withStandardBaseCssClass(withAuthorPanelSwitch(Learning), 'cmp-tabs');
